import { Link } from '@/lib/nav';
import { ArrowRight } from 'lucide-react';
import { Container, Section, Eyebrow } from '@/components/primitives/Container';
import { CitationBlock } from '@/components/citation/CitationBlock';
import { ROUTES } from '@/lib/routes';

const TOC = [
  { id: 'fuentes', label: 'Política de fuentes' },
  { id: 'ingesta', label: 'Ingesta' },
  { id: 'curaduria', label: 'Curaduría humana' },
  { id: 'iac', label: 'Inventario de Activos Curriculares' },
  { id: 'antialucinaciones', label: 'Política contra alucinaciones' },
  { id: 'autoridad', label: 'Cita y autoridad semántica' },
];

export function Metodo() {
  return (
    <>
      <Section className="pt-12 sm:pt-16">
        <Container width="editorial">
          <Eyebrow>Método</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl font-normal tracking-tight sm:text-5xl">
            Cómo SICA garantiza que toda lectura tenga fuente.
          </h1>
          <p className="mt-6 max-w-prose text-lg leading-relaxed text-ink-muted">
            Esta página es densa intencionalmente. Está pensada para auditarse, no para convencer.
          </p>
        </Container>
      </Section>

      <Section className="pt-0">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[14rem_1fr]">
            <aside className="hidden lg:block">
              <nav aria-label="Tabla de contenidos" className="sticky top-24">
                <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
                  En esta página
                </p>
                <ul className="mt-4 flex flex-col gap-2">
                  {TOC.map((t) => (
                    <li key={t.id}>
                      <a
                        href={`#${t.id}`}
                        className="focus-ring rounded text-sm text-ink-muted hover:text-ink"
                      >
                        {t.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <article className="prose-editorial max-w-editorial">
              <section id="fuentes" className="scroll-mt-24">
                <h2 className="font-serif text-2xl font-normal tracking-tight text-ink">
                  Política de fuentes
                </h2>
                <p>
                  Toda respuesta de SICA apunta a un documento y una página verificables. La política
                  define tres compromisos operativos: cómo se almacena el documento original (de forma
                  inmutable, con hash y snapshot), cómo se asocia cada respuesta a un span del
                  documento (a través del Inventario de Activos Curriculares), y cómo se publica la
                  fuente al usuario (siempre visible, nunca opcional).
                </p>
                <CitationBlock
                  citation={{
                    excerpt:
                      'No file will be processed if it is not registered. Citations without a corresponding IAC entry are prohibited.',
                    document: 'SICA · Asset Management',
                    page: 1,
                    jurisdiction: 'Doc interno',
                  }}
                  showSourceLink={false}
                />
              </section>

              <section id="ingesta" className="mt-12 scroll-mt-24">
                <h2 className="font-serif text-2xl font-normal tracking-tight text-ink">
                  Ingesta
                </h2>
                <p>
                  Cada documento normativo se procesa preservando su jerarquía: eje, bloque,
                  saber/capacidad, indicador de progreso. La extracción no aplana. Si un diseño
                  jurisdiccional desarrolla un NAP en términos propios, SICA registra ambos niveles
                  como entidades distintas y conserva la relación entre ellos.
                </p>
                <p>
                  Este principio viene del modelo conceptual del dominio educativo argentino. Las
                  estructuras curriculares no son metadata: son el contenido.
                </p>
              </section>

              <section id="curaduria" className="mt-12 scroll-mt-24">
                <h2 className="font-serif text-2xl font-normal tracking-tight text-ink">
                  Curaduría humana
                </h2>
                <p>
                  Antes de que la jerarquía extraída llegue al motor de consulta, un especialista
                  curricular la valida. La validación cubre denominaciones (¿se llama así en este
                  diseño?), alcances (¿cuál es el ciclo y la modalidad?), y relaciones (¿este saber
                  desarrolla este NAP, o es propio de la jurisdicción?). Sin curaduría, no hay
                  publicación.
                </p>
              </section>

              <section id="iac" className="mt-12 scroll-mt-24">
                <h2 className="font-serif text-2xl font-normal tracking-tight text-ink">
                  Inventario de Activos Curriculares (IAC)
                </h2>
                <p>
                  Cada documento fuente queda registrado de forma inmutable y auditable. El registro
                  incluye hash criptográfico, snapshot del archivo, URL original al portal oficial y
                  fecha de captura. El IAC es el ancla que hace posible la trazabilidad.
                </p>
              </section>

              <section id="antialucinaciones" className="mt-12 scroll-mt-24">
                <h2 className="font-serif text-2xl font-normal tracking-tight text-ink">
                  Política contra alucinaciones
                </h2>
                <p>
                  Si SICA no encuentra un fragmento que respalde la respuesta, lo dice. Esta es la
                  diferencia operativa entre SICA y un LLM genérico. La negativa a inventar es una
                  restricción técnica, no un disclaimer legal: el motor no puede emitir una respuesta
                  que no esté anclada al IAC.
                </p>
              </section>

              <section id="autoridad" className="mt-12 scroll-mt-24">
                <h2 className="font-serif text-2xl font-normal tracking-tight text-ink">
                  Cita y autoridad semántica
                </h2>
                <p>
                  El significado curricular se construye en el Core. La UI lo consume — no lo crea,
                  no lo infiere, no lo curva. Esta separación es lo que permite que la cita tenga
                  valor: si la UI pudiera reescribir la jerarquía, ninguna respuesta sería confiable.
                </p>
                <p className="mt-6">
                  <Link
                    to={ROUTES.producto.jurisdicciones}
                    className="focus-ring inline-flex items-center gap-1 rounded text-sm font-medium text-ink hover:text-accent"
                  >
                    Ver el método aplicado en el mapa de cobertura
                    <ArrowRight size={14} />
                  </Link>
                </p>
              </section>
            </article>
          </div>
        </Container>
      </Section>
    </>
  );
}
